import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Grid, Button, Card, CardContent, CardHeader,
  Select, MenuItem, FormControl, InputLabel, Alert, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, OutlinedInput,
  Checkbox, ListItemText,
} from '@mui/material';
import {
  TrendingUp, TrendingDown, Insights, PieChart, Timeline, AccountBalance,
  TableChart, Close, Download, CalendarToday, FilterList, Clear,
  FileDownload, Assessment,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { analyticsApi, transactionsApi, tagsApi, accountBalancesApi, categoriesApi } from '../services/api';
import { colorFor, formatMoneyShort } from '../utils/chartColors';
import { useNotification } from '../hooks/useNotification';
import { formatCurrency, formatDate } from '../utils/helpers';
import LoadingButton from '../components/LoadingButton';
import BudgetAnalytics from '../components/BudgetAnalytics';
import MoneyMovedCards from '../components/MoneyMovedCards';
import TagChip from '../components/TagChip';

const BALANCE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const Analytics = () => {
  const { showError, showSuccess } = useNotification();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [stats, setStats] = useState({});
  const [balanceTrend, setBalanceTrend] = useState([]);
  const [balanceAccounts, setBalanceAccounts] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    category: [], // Changed to array for multi-select
    accountType: '',
    accountId: [], // Changed to array for multi-select
    tags: [], // Array of tag IDs for filtering
  });
  const [categories, setCategories] = useState([]);
  const [accountIds, setAccountIds] = useState([]);
  const [allTags, setAllTags] = useState([]); // All available tags for filtering
  
  // Transaction Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({
    category: '',
    transactions: [],
    totalAmount: 0,
    count: 0,
    loading: false,
  });

  // Category -> group (for the "by group" chart); a clicked group narrows the category list
  const [categoryGroups, setCategoryGroups] = useState({});
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Load analytics data
  const loadAnalyticsData = useCallback(async (filtersToUse) => {
    try {
      setLoading(true);
      const response = await analyticsApi.getAnalytics(filtersToUse);
      setData(response.data || {});
      setStats(response.stats || {});
      setCategories(response.categories || []);
      setAccountIds(response.accountIds || []);
    } catch (error) {
      console.error('Error loading analytics:', error);
      // showError removed from dependency to prevent infinite loop
      // Error is logged to console instead
    } finally {
      setLoading(false);
    }
  }, []); // Empty deps - function is stable

  const loadAnalytics = (filtersToUse = filters) => {
    loadAnalyticsData(filtersToUse);
  };

  const loadTags = async () => {
    try {
      const response = await tagsApi.getAll();
      setAllTags(response.data.tags || []);
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  const loadBalanceTrend = useCallback(async (filtersToUse = {}) => {
    try {
      const params = {};
      if (filtersToUse.dateFrom) params.from_date = filtersToUse.dateFrom;
      if (filtersToUse.dateTo) params.to_date = filtersToUse.dateTo;
      const response = await accountBalancesApi.getTrend(params);
      const trend = response.data?.trend || [];

      // Group by account and build chart data
      const accountSet = new Set();
      const dateMap = {};
      trend.forEach(item => {
        const acct = item.accountId || item.bank || 'Unknown';
        accountSet.add(acct);
        const dateStr = item.balanceDate;
        if (!dateMap[dateStr]) dateMap[dateStr] = { date: dateStr };
        dateMap[dateStr][acct] = parseFloat(item.balance);
      });

      const accounts = Array.from(accountSet);
      setBalanceAccounts(accounts);
      setBalanceTrend(Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date)));
    } catch (error) {
      console.error('Error loading balance trend:', error);
    }
  }, []);

  useEffect(() => {
    // Load tags and analytics on initial mount
    loadTags();
    loadAnalyticsData({});
    loadBalanceTrend({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array - only run once on mount

  // Cleanup: Clear Analytics params when navigating away
  useEffect(() => {
    return () => {
      // Use replaceState to avoid adding to browser history
      const url = new URL(window.location);
      url.search = ''; // Clear all params
      window.history.replaceState({}, '', url);
    };
  }, []);

  // Filter handlers
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleCategorySelectAll = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (filters.category.length === categories.length) {
      // If all are selected, deselect all
      setFilters(prev => ({ ...prev, category: [] }));
    } else {
      // Select all categories
      setFilters(prev => ({ ...prev, category: [...categories] }));
    }
  };

  const handleAccountIdSelectAll = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (filters.accountId.length === accountIds.length) {
      // If all are selected, deselect all
      setFilters(prev => ({ ...prev, accountId: [] }));
    } else {
      // Select all account IDs
      setFilters(prev => ({ ...prev, accountId: [...accountIds] }));
    }
  };

  const handleTagSelectAll = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (filters.tags.length === allTags.length) {
      // If all are selected, deselect all
      setFilters(prev => ({ ...prev, tags: [] }));
    } else {
      // Select all tags (by ID)
      setFilters(prev => ({ ...prev, tags: allTags.map(tag => tag.id) }));
    }
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      category: [],
      accountType: '',
      accountId: [],
      tags: [],
    });
  };

  const setQuickDateFilter = (period) => {
    const today = new Date();
    let fromDate;
    
    switch (period) {
      case 'last30':
        fromDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'thisYear':
        fromDate = new Date(today.getFullYear(), 0, 1);
        break;
      default:
        return;
    }
    
    setFilters(prev => ({
      ...prev,
      dateFrom: fromDate.toISOString().split('T')[0],
      dateTo: today.toISOString().split('T')[0],
    }));
  };

  useEffect(() => {
    categoriesApi.getAll({ includeHidden: true })
      .then(response => {
        const groups = {};
        (response.data.categories || []).forEach(c => { groups[c.name] = c.groupName || 'Other'; });
        setCategoryGroups(groups);
      })
      .catch(() => setCategoryGroups({}));
  }, []);

  // Spending per category group, biggest first
  const groupData = (() => {
    const totals = {};
    (data.categoryPie || []).forEach(item => {
      const group = categoryGroups[item.name] || 'Other';
      totals[group] = (totals[group] || 0) + item.value;
    });
    return Object.entries(totals)
      .map(([name, value]) => ({ name, value }))
      .sort((x, y) => y.value - x.value);
  })();
  const groupTotal = groupData.reduce((sum, g) => sum + g.value, 0);
  const visibleCategories = (data.categoryPie || [])
    .filter(item => !selectedGroup || (categoryGroups[item.name] || 'Other') === selectedGroup);

  // Pie chart click handler
  const handlePieClick = async (data, index) => {
    if (!data || !data.payload) return;
    
    const category = data.payload.name;
    console.log('Pie clicked for category:', category, 'with filters:', filters);
    setModalData(prev => ({ ...prev, loading: true, category }));
    setModalOpen(true);
    
    try {
      // Transform filters to match backend API expectations
      const params = {
        type: 'expense',
        category, // Override with clicked category (single value)
        limit: 10000 // Get all transactions (high limit to avoid pagination)
      };
      
      // Convert date filters (dateFrom/dateTo -> startDate/endDate)
      if (filters.dateFrom) {
        params.startDate = filters.dateFrom;
      }
      if (filters.dateTo) {
        params.endDate = filters.dateTo;
      }
      
      // Handle accountType filter (single value)
      if (filters.accountType) {
        params.accountType = filters.accountType;
      }
      
      // Handle accountId filter (backend now supports arrays)
      if (filters.accountId && filters.accountId.length > 0) {
        // Pass array directly - backend handles both single values and arrays
        params.accountId = filters.accountId;
      }

      // Handle tags filter
      if (filters.tags && filters.tags.length > 0) {
        // Pass comma-separated tag IDs
        params.tags = filters.tags.join(',');
      }
      
      console.log('Fetching transactions with transformed params:', params);
      const response = await transactionsApi.getAll(params);
      console.log('Response:', response);
      
      // The backend returns { transactions: [...], count, summary, pagination }
      const categoryTransactions = response.data?.transactions || [];
      console.log('Category transactions found:', categoryTransactions.length);
      
      const totalAmount = categoryTransactions.reduce((sum, t) => sum + Math.abs(t.amount), 0);
      
      setModalData({
        category,
        transactions: categoryTransactions,
        totalAmount,
        count: categoryTransactions.length,
        loading: false,
      });
    } catch (error) {
      console.error('Error loading category transactions:', error);
      showError('Failed to load transaction details');
      setModalData(prev => ({ ...prev, loading: false }));
    }
  };

  // Export functions
  const exportData = async (format) => {
    try {
      const response = await analyticsApi.exportData({ ...filters, format });
      // Create download link
      const contentType = format === 'csv' ? 'text/csv' : 'text/plain';
      const extension = format === 'csv' ? 'csv' : 'txt';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics_${new Date().toISOString().split('T')[0]}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSuccess(`Analytics exported as ${format === 'csv' ? 'CSV' : 'Report'}`);
    } catch (error) {
      console.error('Export error:', error);
      showError('Failed to export data');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2, mb: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 2, px: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          <Assessment sx={{ mr: { xs: 1, sm: 2 }, verticalAlign: 'middle', fontSize: { xs: '1.75rem', sm: 'inherit' } }} />
          Analytics Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          Interactive data visualization and insights
        </Typography>
      </Box>

      {/* Filter Section */}
      <Card sx={{ mb: 3, mx: 2 }}>
        <CardHeader 
          title={
            <Box display="flex" alignItems="center">
              <FilterList sx={{ mr: 1 }} />
              Filter Analytics Data
            </Box>
          }
        />
        <CardContent>
          <form onSubmit={(e) => { e.preventDefault(); loadAnalytics(filters); loadBalanceTrend(filters); }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="medium"
                sx={{ minWidth: 160 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="medium"
                sx={{ minWidth: 160 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
              <FormControl fullWidth size="medium" sx={{ minWidth: 180 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  multiple
                  value={filters.category}
                  label="Category"
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  input={<OutlinedInput label="Category" />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <Typography variant="body2" color="text.secondary">All Categories</Typography>;
                    }
                    if (selected.length === categories.length) {
                      return <Typography variant="body2">All Categories ({selected.length})</Typography>;
                    }
                    if (selected.length > 3) {
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', overflow: 'hidden' }}>
                          <Chip label={selected[0]} size="small" />
                          <Chip label={selected[1]} size="small" />
                          <Chip label={selected[2]} size="small" />
                          <Typography variant="body2" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>
                            +{selected.length - 3} more
                          </Typography>
                        </Box>
                      );
                    }
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden' }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {/* Select All Option */}
                  <MenuItem 
                    onMouseDown={handleCategorySelectAll}
                    sx={{ 
                      borderBottom: '1px solid #e0e0e0',
                      fontWeight: 'bold',
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <Checkbox
                      checked={filters.category.length === categories.length && categories.length > 0}
                      indeterminate={filters.category.length > 0 && filters.category.length < categories.length}
                    />
                    <ListItemText primary="Select All" />
                  </MenuItem>
                  
                  {/* Individual Categories */}
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      <Checkbox checked={filters.category.indexOf(cat) > -1} />
                      <ListItemText primary={cat} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <FormControl fullWidth size="medium" sx={{ minWidth: 200 }}>
                <InputLabel>Account Type</InputLabel>
                <Select
                  value={filters.accountType}
                  label="Account Type"
                  onChange={(e) => handleFilterChange('accountType', e.target.value)}
                >
                  <MenuItem value="">All Account Types</MenuItem>
                  <MenuItem value="bank_account">Bank Account</MenuItem>
                  <MenuItem value="credit_card">Credit Card</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <FormControl fullWidth size="medium" sx={{ minWidth: 180 }}>
                <InputLabel>Account ID</InputLabel>
                <Select
                  multiple
                  value={filters.accountId}
                  label="Account ID"
                  onChange={(e) => handleFilterChange('accountId', e.target.value)}
                  input={<OutlinedInput label="Account ID" />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <Typography variant="body2" color="text.secondary">All Account IDs</Typography>;
                    }
                    if (selected.length === accountIds.length) {
                      return <Typography variant="body2">All Account IDs ({selected.length})</Typography>;
                    }
                    if (selected.length > 3) {
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', overflow: 'hidden' }}>
                          <Chip label={selected[0]} size="small" />
                          <Chip label={selected[1]} size="small" />
                          <Chip label={selected[2]} size="small" />
                          <Typography variant="body2" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>
                            +{selected.length - 3} more
                          </Typography>
                        </Box>
                      );
                    }
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden' }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {/* Select All Option */}
                  <MenuItem 
                    onMouseDown={handleAccountIdSelectAll}
                    sx={{ 
                      borderBottom: '1px solid #e0e0e0',
                      fontWeight: 'bold',
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <Checkbox
                      checked={filters.accountId.length === accountIds.length && accountIds.length > 0}
                      indeterminate={filters.accountId.length > 0 && filters.accountId.length < accountIds.length}
                    />
                    <ListItemText primary="Select All" />
                  </MenuItem>
                  
                  {/* Individual Account IDs */}
                  {accountIds.map((id) => (
                    <MenuItem key={id} value={id}>
                      <Checkbox checked={filters.accountId.indexOf(id) > -1} />
                      <ListItemText primary={id} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
              <FormControl fullWidth size="medium" sx={{ minWidth: 200 }}>
                <InputLabel>Tags</InputLabel>
                <Select
                  multiple
                  value={filters.tags}
                  label="Tags"
                  onChange={(e) => handleFilterChange('tags', e.target.value)}
                  input={<OutlinedInput label="Tags" />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <Typography variant="body2" color="text.secondary">All Tags</Typography>;
                    }
                    if (selected.length === allTags.length) {
                      return <Typography variant="body2">All Tags ({selected.length})</Typography>;
                    }
                    const selectedTags = allTags.filter(tag => selected.includes(tag.id));
                    if (selectedTags.length > 3) {
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', overflow: 'hidden' }}>
                          <TagChip tag={selectedTags[0]} size="small" />
                          <TagChip tag={selectedTags[1]} size="small" />
                          <TagChip tag={selectedTags[2]} size="small" />
                          <Typography variant="body2" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>
                            +{selectedTags.length - 3} more
                          </Typography>
                        </Box>
                      );
                    }
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden' }}>
                        {selectedTags.map((tag) => (
                          <TagChip key={tag.id} tag={tag} size="small" />
                        ))}
                      </Box>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {/* Select All Option */}
                  <MenuItem 
                    onMouseDown={handleTagSelectAll}
                    sx={{ 
                      borderBottom: '1px solid #e0e0e0',
                      fontWeight: 'bold',
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <Checkbox
                      checked={filters.tags.length === allTags.length && allTags.length > 0}
                      indeterminate={filters.tags.length > 0 && filters.tags.length < allTags.length}
                    />
                    <ListItemText primary="Select All" />
                  </MenuItem>
                  
                  {/* Individual Tags */}
                  {allTags.map((tag) => (
                    <MenuItem key={tag.id} value={tag.id}>
                      <Checkbox checked={filters.tags.indexOf(tag.id) > -1} />
                      <ListItemText 
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TagChip tag={tag} size="small" />
                          </Box>
                        } 
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
            <LoadingButton
              variant="contained"
              type="submit"
              startIcon={<FilterList />}
              size="small"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Apply Filters
            </LoadingButton>
            <Button
              variant="outlined"
              type="button"
              onClick={clearFilters}
              startIcon={<Clear />}
              size="small"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Clear Filters
            </Button>
            <Button
              variant="outlined"
              type="button"
              onClick={() => setQuickDateFilter('last30')}
              startIcon={<CalendarToday />}
              size="small"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Last 30 Days
            </Button>
            <Button
              variant="outlined"
              type="button"
              onClick={() => setQuickDateFilter('thisYear')}
              startIcon={<CalendarToday />}
              size="small"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              This Year
            </Button>
          </Box>
          </form>
        </CardContent>
      </Card>

      {/* Key Metrics Cards */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3, px: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card sx={{ height: '100%', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Transactions</Typography>
                  <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>{stats.totalTransactions || 0}</Typography>
                </Box>
                <TableChart sx={{ fontSize: { xs: 36, sm: 48 }, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card sx={{ height: '100%', bgcolor: 'success.main', color: 'success.contrastText' }}>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Total Credits</Typography>
                  <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>{formatCurrency(stats.totalCredits || 0)}</Typography>
                </Box>
                <TrendingUp sx={{ fontSize: { xs: 36, sm: 48 }, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card sx={{ height: '100%', bgcolor: 'error.main', color: 'error.contrastText' }}>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Total Expenses</Typography>
                  <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>{formatCurrency(Math.abs(stats.totalExpenses || 0))}</Typography>
                </Box>
                <TrendingDown sx={{ fontSize: { xs: 36, sm: 48 }, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card sx={{ height: '100%', bgcolor: 'info.main', color: 'info.contrastText' }}>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Net Balance</Typography>
                  <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>{formatCurrency(stats.netBalance || 0)}</Typography>
                </Box>
                <AccountBalance sx={{ fontSize: { xs: 36, sm: 48 }, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card sx={{ height: '100%', bgcolor: 'warning.main', color: 'warning.contrastText' }}>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Avg Transaction</Typography>
                  <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>{formatCurrency(stats.avgTransaction || 0)}</Typography>
                </Box>
                <Insights sx={{ fontSize: { xs: 36, sm: 48 }, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Money that left your bank accounts, and what it did */}
      <MoneyMovedCards moneyOut={stats.bankMoneyOut} sx={{ mb: 3, px: 2 }} />

      {/* Data Period Info */}
      {stats.dateRangeStart && stats.dateRangeEnd && (
        <Alert severity="info" sx={{ mb: 3, mx: 2 }}>
          <strong>Data Period:</strong> {formatDate(stats.dateRangeStart)} to {formatDate(stats.dateRangeEnd)}
          {(filters.dateFrom || filters.dateTo) && (
            <>
              {' | '}
              <strong>Current Filter:</strong>{' '}
              {filters.dateFrom && `From ${formatDate(filters.dateFrom)}`}
              {filters.dateTo && `${filters.dateFrom ? ' ' : ''}To ${formatDate(filters.dateTo)}`}
            </>
          )}
        </Alert>
      )}

      {/* Charts Section - Row 1: Category Pie Chart and Category Bar Chart */}
      <Grid container spacing={2} sx={{ mb: 2, px: 2 }}>
        {/* Spending by group: horizontal bars, click a group to see its categories */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: { xs: 400, sm: 450, md: 500 } }}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center">
                  <Assessment sx={{ mr: 1 }} />
                  Spending by Group
                </Box>
              }
              subheader="Click a group to see its categories"
              sx={{ pb: 0, pt: 1 }}
            />
            <CardContent sx={{ height: 420, p: 1, m: 0 }}>
              {groupData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={groupData} layout="vertical" margin={{ left: 8, right: 24, top: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                    <XAxis type="number" tickFormatter={formatMoneyShort} tick={{ fontSize: 11, fill: '#334155' }} stroke="#94a3b8" />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12, fill: '#334155' }} stroke="#94a3b8" />
                    <Tooltip
                      formatter={(value) => [
                        `${formatCurrency(value)} (${groupTotal ? ((value / groupTotal) * 100).toFixed(1) : 0}%)`,
                        'Spent'
                      ]}
                      contentStyle={{ fontSize: '12px', borderRadius: '8px' }}
                    />
                    <Bar
                      dataKey="value"
                      radius={[0, 6, 6, 0]}
                      cursor="pointer"
                      onClick={(entry) => setSelectedGroup(prev => (prev === entry.name ? null : entry.name))}
                    >
                      {groupData.map(entry => (
                        <Cell
                          key={entry.name}
                          fill={colorFor(entry.name)}
                          fillOpacity={!selectedGroup || selectedGroup === entry.name ? 1 : 0.35}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No spending in this period</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Category Bar Chart - Scrollable */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: { xs: 400, sm: 450, md: 500 } }}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center">
                    <PieChart sx={{ mr: 1 }} />
                    Spending by Category
                  </Box>
                  {selectedGroup && (
                    <Chip size="small" label={selectedGroup} onDelete={() => setSelectedGroup(null)} />
                  )}
                </Box>
              }
              sx={{ pb: 0, pt: 1 }}
            />
            <CardContent sx={{ height: 420, p: 2, overflow: 'hidden' }}>
              {data.categoryPie && data.categoryPie.length > 0 ? (
                <Box 
                  sx={{ 
                    height: '100%', 
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    pr: 1,
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'rgba(6, 182, 212, 0.1)',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(6, 182, 212, 0.3)',
                      borderRadius: '4px',
                      '&:hover': {
                        background: 'rgba(6, 182, 212, 0.5)',
                      },
                    },
                  }}
                >
                  {visibleCategories.map((item, index) => {
                    const total = data.categoryPie.reduce((sum, cat) => sum + cat.value, 0);
                    const percentage = ((item.value / total) * 100).toFixed(1);
                    return (
                      <Box 
                        key={item.name} 
                        sx={{ 
                          mb: 2,
                          cursor: 'pointer',
                          '&:hover': {
                            '& .category-bar-fill': {
                              filter: 'brightness(1.1)',
                            }
                          }
                        }}
                        onClick={() => handlePieClick({ payload: item }, index)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: colorFor(item.name),
                                flexShrink: 0
                              }}
                            />
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                              {item.name}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                            {formatCurrency(item.value)}
                          </Typography>
                        </Box>
                        <Box 
                          sx={{ 
                            height: 8, 
                            backgroundColor: 'rgba(6, 182, 212, 0.1)', 
                            borderRadius: '4px',
                            overflow: 'hidden',
                            position: 'relative',
                            mb: 0.5
                          }}
                        >
                          <Box
                            className="category-bar-fill"
                            sx={{
                              height: '100%',
                              width: `${percentage}%`,
                              backgroundColor: colorFor(item.name),
                              borderRadius: '4px',
                              transition: 'all 0.3s ease-out',
                              animation: 'fillBar 1s ease-out forwards',
                              animationDelay: `${index * 0.1}s`,
                              '@keyframes fillBar': {
                                from: {
                                  width: '0%',
                                  opacity: 0,
                                },
                                to: {
                                  opacity: 1,
                                },
                              }
                            }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.disabled', fontWeight: 600 }}>
                          {percentage}%
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No category data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section - Row 2: Monthly Trend and Income vs Expenses */}
      <Grid container spacing={2} sx={{ mb: 2, px: 2 }}>
        {/* Monthly Trend */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: { xs: 400, sm: 450, md: 500 } }}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center">
                  <Timeline sx={{ mr: 1 }} />
                  Monthly Expenditure Trend
                </Box>
              }
              sx={{ pb: 0, pt: 1 }}
            />
            <CardContent sx={{ height: 420, p: 0, m: 0 }}>
              {data.monthlyTrend && data.monthlyTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.monthlyTrend}>
                    <defs>
                      <linearGradient id="trendLineGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={1} />
                        <stop offset="100%" stopColor="#8b5cf6" stopOpacity={1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(6, 182, 212, 0.1)" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#334155' }} stroke="#94a3b8" />
                    <YAxis tickFormatter={formatMoneyShort} tick={{ fontSize: 11, fill: '#334155' }} stroke="#94a3b8" />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Amount']}
                      contentStyle={{ 
                        fontSize: '12px', 
                        backgroundColor: 'rgba(30, 41, 59, 0.95)', 
                        border: '1px solid rgba(6, 182, 212, 0.3)', 
                        borderRadius: '8px',
                        color: '#ffffff'
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line 
                      type="monotone" 
                      dataKey="amount" 
                      stroke="url(#trendLineGradient)" 
                      strokeWidth={3}
                      dot={{ fill: '#3b82f6', strokeWidth: 2, r: 5 }}
                      activeDot={{ r: 7 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No trend data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Income vs Expenses */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: { xs: 400, sm: 450, md: 500 } }}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center">
                  <TrendingUp sx={{ mr: 1 }} />
                  Monthly income and money out
                </Box>
              }
              sx={{ pb: 0, pt: 1 }}
            />
            <CardContent sx={{ height: 420, p: 0, m: 0 }}>
              {data.incomeExpense && data.incomeExpense.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.incomeExpense}>
                    <defs>
                      <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={1} />
                        <stop offset="100%" stopColor="#34d399" stopOpacity={1} />
                      </linearGradient>
                      <linearGradient id="expensesGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ef4444" stopOpacity={1} />
                        <stop offset="100%" stopColor="#f87171" stopOpacity={1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(6, 182, 212, 0.1)" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#334155' }} stroke="#94a3b8" />
                    <YAxis tickFormatter={formatMoneyShort} tick={{ fontSize: 11, fill: '#334155' }} stroke="#94a3b8" />
                    <Tooltip
                      formatter={(value, name) => [formatCurrency(Math.abs(value)), name]}
                      contentStyle={{
                        fontSize: '12px',
                        backgroundColor: 'rgba(30, 41, 59, 0.95)',
                        border: '1px solid rgba(6, 182, 212, 0.3)',
                        borderRadius: '8px',
                        color: '#ffffff'
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="income" fill="url(#incomeGradient)" name="Income" radius={[8, 8, 0, 0]} />
                    {/* Everything that left, stacked: spending, then invested and big purchases on top */}
                    <Bar dataKey="expenses" stackId="out" fill="url(#expensesGradient)" name="Spending" />
                    <Bar dataKey="invested" stackId="out" fill="#0ea5e9" name="Invested" />
                    <Bar dataKey="bigPurchases" stackId="out" fill="#8b5cf6" name="Big purchases" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No income/expense data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section - Row 3: Top Categories Trend and Account ID Analysis */}
      <Grid container spacing={2} sx={{ mb: 2, px: 2 }}>
        {/* Top Categories Trend */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: { xs: 400, sm: 450, md: 500 } }}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center">
                  <TrendingDown sx={{ mr: 1 }} />
                  Top Categories Trend
                </Box>
              }
              sx={{ pb: 0, pt: 1 }}
            />
            <CardContent sx={{ height: 420, p: 0, m: 0 }}>
              {data.categoryTrend && data.categoryTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.categoryTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(6, 182, 212, 0.1)" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#334155' }} stroke="#94a3b8" />
                    <YAxis tickFormatter={formatMoneyShort} tick={{ fontSize: 11, fill: '#334155' }} stroke="#94a3b8" />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Amount']}
                      contentStyle={{ 
                        fontSize: '12px', 
                        backgroundColor: 'rgba(30, 41, 59, 0.95)', 
                        border: '1px solid rgba(6, 182, 212, 0.3)', 
                        borderRadius: '8px',
                        color: '#ffffff'
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    {data.topCategories?.slice(0, 5).map((category, index) => (
                      <Line
                        key={category}
                        type="monotone"
                        dataKey={category}
                        stroke={colorFor(category)}
                        strokeWidth={3}
                        dot={{ fill: colorFor(category), strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No category trend data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Account ID Analysis */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: { xs: 400, sm: 450, md: 500 } }}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center">
                  <TableChart sx={{ mr: 1 }} />
                  Account ID-wise Analysis
                </Box>
              }
              sx={{ pb: 0, pt: 1 }}
            />
            <CardContent sx={{ height: 420, p: 0, m: 0 }}>
              {data.accountIdAnalysis && data.accountIdAnalysis.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.accountIdAnalysis}>
                    <defs>
                      <linearGradient id="accountCreditsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={1} />
                        <stop offset="100%" stopColor="#34d399" stopOpacity={1} />
                      </linearGradient>
                      <linearGradient id="accountExpensesGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ef4444" stopOpacity={1} />
                        <stop offset="100%" stopColor="#f87171" stopOpacity={1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(6, 182, 212, 0.1)" />
                    <XAxis dataKey="accountId" tick={{ fontSize: 11, fill: '#334155' }} stroke="#94a3b8" />
                    <YAxis tickFormatter={formatMoneyShort} tick={{ fontSize: 11, fill: '#334155' }} stroke="#94a3b8" />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Amount']}
                      contentStyle={{ 
                        fontSize: '12px', 
                        backgroundColor: 'rgba(30, 41, 59, 0.95)', 
                        border: '1px solid rgba(6, 182, 212, 0.3)', 
                        borderRadius: '8px',
                        color: '#ffffff'
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="credits" fill="url(#accountCreditsGradient)" name="Credits" radius={[8, 8, 0, 0]} />
                    <Bar dataKey="expenses" fill="url(#accountExpensesGradient)" name="Expenses" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No account ID analysis data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section - Row 4: Day of Week and Bank Analysis */}
      <Grid container spacing={2} sx={{ mb: 4, px: 2 }}>
        {/* Day of Week Spending */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: { xs: 400, sm: 450, md: 500 } }}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center">
                  <CalendarToday sx={{ mr: 1 }} />
                  Spending by Day of Week
                </Box>
              }
              sx={{ pb: 0, pt: 1 }}
            />
            <CardContent sx={{ height: 420, p: 0, m: 0 }}>
              {data.dayOfWeek && data.dayOfWeek.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.dayOfWeek}>
                    <defs>
                      <linearGradient id="daySpendingGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8b5cf6" stopOpacity={1} />
                        <stop offset="100%" stopColor="#a78bfa" stopOpacity={1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(6, 182, 212, 0.1)" />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#334155' }} stroke="#94a3b8" />
                    <YAxis tickFormatter={formatMoneyShort} tick={{ fontSize: 11, fill: '#334155' }} stroke="#94a3b8" />
                    <Tooltip 
                      formatter={(value, name) => [
                        name === 'amount' ? formatCurrency(value) : value,
                        name === 'amount' ? 'Total Spent' : name === 'count' ? 'Transactions' : 'Average'
                      ]}
                      contentStyle={{ 
                        fontSize: '12px', 
                        backgroundColor: 'rgba(30, 41, 59, 0.95)', 
                        border: '1px solid rgba(6, 182, 212, 0.3)', 
                        borderRadius: '8px',
                        color: '#ffffff'
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="amount" fill="url(#daySpendingGradient)" name="Total Spent" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No day of week data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Bank Analysis */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: { xs: 400, sm: 450, md: 500 } }}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center">
                  <AccountBalance sx={{ mr: 1 }} />
                  Bank-wise Analysis
                </Box>
              }
              sx={{ pb: 0, pt: 1 }}
            />
            <CardContent sx={{ height: 420, p: 0, m: 0 }}>
              {data.bankAnalysis && data.bankAnalysis.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.bankAnalysis}>
                    <defs>
                      <linearGradient id="bankCreditsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#06b6d4" stopOpacity={1} />
                        <stop offset="100%" stopColor="#22d3ee" stopOpacity={1} />
                      </linearGradient>
                      <linearGradient id="bankExpensesGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f59e0b" stopOpacity={1} />
                        <stop offset="100%" stopColor="#fbbf24" stopOpacity={1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(6, 182, 212, 0.1)" />
                    <XAxis dataKey="bank" tick={{ fontSize: 11, fill: '#334155' }} stroke="#94a3b8" />
                    <YAxis tickFormatter={formatMoneyShort} tick={{ fontSize: 11, fill: '#334155' }} stroke="#94a3b8" />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Amount']}
                      contentStyle={{ 
                        fontSize: '12px', 
                        backgroundColor: 'rgba(30, 41, 59, 0.95)', 
                        border: '1px solid rgba(6, 182, 212, 0.3)', 
                        borderRadius: '8px',
                        color: '#ffffff'
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="credits" fill="url(#bankCreditsGradient)" name="Credits" radius={[8, 8, 0, 0]} />
                    <Bar dataKey="expenses" fill="url(#bankExpensesGradient)" name="Expenses" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No bank analysis data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Balance Trend Chart */}
      {balanceTrend.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2, px: 2 }}>
          <Grid size={{ xs: 12 }}>
            <Card sx={{ height: { xs: 400, sm: 450, md: 500 } }}>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center">
                    <AccountBalance sx={{ mr: 1 }} />
                    Account Balance Trend
                  </Box>
                }
                sx={{ pb: 0, pt: 1 }}
              />
              <CardContent sx={{ height: 420, p: 0, m: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={balanceTrend}>
                    <defs>
                      {balanceAccounts.map((acct, idx) => (
                        <linearGradient key={acct} id={`balGrad${idx}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={BALANCE_COLORS[idx % BALANCE_COLORS.length]} stopOpacity={0.3} />
                          <stop offset="100%" stopColor={BALANCE_COLORS[idx % BALANCE_COLORS.length]} stopOpacity={0.05} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(6, 182, 212, 0.1)" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#334155' }}
                      stroke="#94a3b8"
                      tickFormatter={(d) => {
                        const dt = new Date(d);
                        return `${dt.getDate()}/${dt.getMonth() + 1}`;
                      }}
                    />
                    <YAxis tickFormatter={formatMoneyShort} tick={{ fontSize: 11, fill: '#334155' }} stroke="#94a3b8" />
                    <Tooltip
                      formatter={(value, name) => [formatCurrency(value), name]}
                      labelFormatter={(label) => formatDate(label)}
                      contentStyle={{
                        fontSize: '12px',
                        backgroundColor: 'rgba(30, 41, 59, 0.95)',
                        border: '1px solid rgba(6, 182, 212, 0.3)',
                        borderRadius: '8px',
                        color: '#ffffff',
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    {balanceAccounts.map((acct, idx) => (
                      <Area
                        key={acct}
                        type="monotone"
                        dataKey={acct}
                        name={acct}
                        stroke={BALANCE_COLORS[idx % BALANCE_COLORS.length]}
                        fill={`url(#balGrad${idx})`}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Budget Analytics Section */}
      <Box sx={{ mb: 4, px: 2 }}>
        <BudgetAnalytics />
      </Box>

      {/* Export Section */}
      <Card sx={{ mb: 4, mx: 2 }}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center">
              <Download sx={{ mr: 1 }} />
              Export Options
            </Box>
          }
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Export your analytics data and visualizations
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              color="success"
              startIcon={<FileDownload />}
              onClick={() => exportData('csv')}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FileDownload />}
              onClick={() => exportData('pdf')}
            >
              Export Report
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={() => window.print()}
            >
              Print Charts
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Transaction Details Modal */}
      <Dialog 
        open={modalOpen} 
        onClose={() => setModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center">
              <PieChart sx={{ mr: 2 }} />
              Category Transactions
            </Box>
            <IconButton onClick={() => setModalOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Category Summary */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <strong>Category:</strong> {modalData.category}
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <strong>Total Amount:</strong> {formatCurrency(modalData.totalAmount)}
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <strong>Transaction Count:</strong> {modalData.count}
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <strong>Average:</strong> {formatCurrency(modalData.count > 0 ? modalData.totalAmount / modalData.count : 0)}
              </Grid>
            </Grid>
          </Alert>

          {modalData.loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading transactions...</Typography>
            </Box>
          ) : modalData.transactions.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <Typography color="text.secondary">No transactions found for this category.</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Bank</TableCell>
                    <TableCell>Account</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalData.transactions.slice(0, 100).map((transaction, index) => (
                    <TableRow key={transaction.id || index}>
                      <TableCell>{formatDate(transaction.date)}</TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            maxWidth: 250, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap' 
                          }}
                          title={transaction.description}
                        >
                          {transaction.description}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(Math.abs(transaction.amount))}</strong>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={transaction.bank || 'Unknown'} 
                          size="small" 
                          color="secondary" 
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {transaction.accountType} {transaction.accountId ? `(${transaction.accountId})` : ''}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<Download />}
            onClick={() => exportData('csv')}
            disabled={modalData.transactions.length === 0}
          >
            Export These Transactions
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Analytics;